import React, { ChangeEvent, useState } from "react";
import Cropper from "react-easy-crop";
import { Modal } from "react-bootstrap";

import Back from "assets/images/svg/back_blue.svg";
import ScaleUp from "assets/images/svg/scale_up.svg";
import ScaleDown from "assets/images/svg/scale_down.svg";

import _pictureService from "components/picture-modal/picture.service";
import {useTranslation} from "react-i18next";

type Props = {
  show: boolean;
  hide: () => void;
  title: string;
  src?: string;
  onSubmit: (file: Blob,name:string) => void;
};

const PictureModal = ({ show, hide, title, src = "/", onSubmit }: Props) => {
  const {t} = useTranslation('profile');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [error, setError] = useState("");

  const [picture, setPicture] = React.useState<File | undefined>();
  const [pictureUrl, setPictureUrl] = React.useState("");

  const [zoom, setZoom] = React.useState(1);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [croppedPixels, setCroppedPixels] = useState();

  const resetModal = () => {
    setError("");
    setPicture(undefined);
    setPictureUrl("");
  };

  async function handleImageChange(e: ChangeEvent<HTMLInputElement>) {
    resetModal();
    const file = e.target.files?.[0];
    if (!file) return;

    const isValidType = /.*image.*/i.test(file.type);
    if (!isValidType) {
      resetModal();
      return setError("Invalid Type");
    }

    if (file.size > 1024 * 1024) {
      resetModal();
      return setError("Big Size");
    }

    setPicture(file);
    setPictureUrl(await _pictureService.readFile(file));
  }

  const handleCrop = React.useCallback(
    (_:any, croppedPixels:any) => setCroppedPixels(croppedPixels),
    []
  );

  async function handleImageUpload() {
    if (!picture) return;
    
    const croppedPicture = await _pictureService.getCroppedImage(
      pictureUrl,
      croppedPixels
    );
    if (!croppedPicture) return;
    onSubmit(croppedPicture,picture.name);
  }

  const zoomIn = () => setZoom((z) => (z < 2 ? z + 0.1 : z));
  const zoomOut = () => setZoom((z) => (z > 0.5 ? z - 0.1 : z));


  return (
    <Modal
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={() => {
        resetModal();
        hide();
      }}
      className="all-modal change-pic"
    >
      <Modal.Body>
        <div className="header">
          <img src={Back} alt="back" onClick={hide} />
          <h1>{title}</h1>
        </div>

        <div className="row">
          <div className="col-12">
            <div className={"img-thumbnail " + (error ? "error" : "")}>
              <Cropper
                style={{ cropAreaStyle: { display: "none" } }}
                image={pictureUrl ? pictureUrl : src}
                objectFit="vertical-cover"
                zoom={zoom}
                crop={crop}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCrop}
              />
            </div>
            <p className="text-center text-error">{error}</p>
            <div className="prog">
              <button className="zoom-button" onClick={zoomOut}>
                <img src={ScaleDown} alt="ScaleUp" />
              </button>
              <input
                type="range"
                step={0.1}
                value={zoom}
                id="zoomSlider"
                min={1}
                max={2}
                className="slider"
                onChange={(e) => setZoom(+e.target.value)}
              />
              <button className="zoom-button" onClick={zoomIn}>
                <img src={ScaleUp} alt="ScaleUp" />
              </button>
            </div>
            <p className="p-info">{picture?.name ?? t('choose-file') as string}</p>
            <input
              type="file"
              hidden
              ref={inputRef}
              onChange={handleImageChange}
              accept="image/*"
            />
          </div>

          <div className="col-12">
            <button
              className="btn-White"
              onClick={() => inputRef.current?.click()}
            >
              {t('choose-file') as string}
            </button>
          </div>
          <div className="col-12">
            <button className="btn-blue" onClick={handleImageUpload} disabled={!picture}>
              {t('set-picture') as string}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PictureModal;
